import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useAuthSessionStore from "../stores/auth-session-store";
import useUserStore from "../stores/user-store";
import {
    beginPasskeyActivationRequest,
    beginPasskeyEnrollmentRequest,
    beginPasskeyLoginRequest,
    beginPasswordLoginRequest,
    changeCurrentPasswordRequest,
    completePasskeyActivationRequest,
    completePasskeyEnrollmentRequest,
    completePasskeyLoginRequest,
    deletePasskeyRequest,
    discoverLoginMethodsRequest,
    getCurrentAccountRequest,
    getPasskeySecuritySummaryRequest,
    logoutAllSessionsRequest,
    logoutRequest,
    recoveryCodeLoginRequest,
    regenerateRecoveryCodesRequest,
    resendPasswordLoginCodeRequest,
    updateCurrentAccountRequest,
    verifyPasswordLoginCodeRequest
} from "./auth.api";
import { clearAuthSession, applyAuthSession, isRefreshAuthFailure, refreshAccessToken, shouldBootstrapAuthSession } from "./auth-session";
import { keys } from "./keys";
import { authenticateWithPasskey, registerPasskey } from "./webauthn";

const bootstrapAuthSession = async () => {
    useAuthSessionStore.getState().startSessionBootstrap();
    useAuthSessionStore.getState().setSessionBootstrapStep("checkingSavedSession");

    try {
        return await refreshAccessToken();
    } catch (error) {
        const hasRememberedSession = useUserStore.getState().loggedIn;

        if (hasRememberedSession && isRefreshAuthFailure(error)) {
            useAuthSessionStore.getState().openReauthModal();
            return null;
        }

        clearAuthSession();
        return null;
    } finally {
        useAuthSessionStore.getState().finishSessionBootstrap();
    }
};

export const useBootstrapAuthSessionQuery = () => {
    const sessionBootstrapStatus = useAuthSessionStore((state) => state.sessionBootstrapStatus);
    const shouldBootstrap = sessionBootstrapStatus === "idle" && shouldBootstrapAuthSession();

    return useQuery({
        queryKey: keys.auth.session(),
        queryFn: bootstrapAuthSession,
        enabled: shouldBootstrap,
        retry: false,
        staleTime: Infinity,
        gcTime: Infinity,
        refetchOnMount: false,
        refetchOnWindowFocus: false
    });
};

export const useLoginMutation = (successCallback = null, errorCallback = null) => {
    return useMutation({
        mutationFn: beginPasswordLoginRequest,
        onSuccess: (data) => {
            if (successCallback) {
                successCallback(data);
            }
        },
        onError: (error) => {
            if (errorCallback) {
                errorCallback(error);
            }
        }
    });
};

export const useDiscoverLoginMethodsMutation = (successCallback = null, errorCallback = null) => {
    return useMutation({
        mutationFn: discoverLoginMethodsRequest,
        onSuccess: (data) => {
            if (successCallback) {
                successCallback(data);
            }
        },
        onError: (error) => {
            if (errorCallback) {
                errorCallback(error);
            }
        }
    });
};

export const useCurrentAccountQuery = (enabled = true) => {
    return useQuery({
        queryKey: keys.auth.account(),
        queryFn: getCurrentAccountRequest,
        enabled
    });
};

const setAuthenticatedSession = (qc, session, successCallback = null) => {
    applyAuthSession(session);
    qc.setQueryData(keys.auth.session(), session);

    if (successCallback) {
        successCallback(session);
    }
};

const setAccountPasskeyConfigured = (qc) => {
    qc.setQueryData(keys.auth.account(), (account) => {
        if (!account) {
            return account;
        }

        return {
            ...account,
            passkeyConfigured: true,
            passwordChangeAvailable: false,
            passwordExpiresAt: null,
            passwordExpired: false,
            passwordExpiresSoon: false,
            passwordExpiresInDays: null
        };
    });
};

const refreshPasskeyAccountState = (qc) => {
    qc.invalidateQueries({ queryKey: keys.auth.account() });
    qc.invalidateQueries({ queryKey: keys.auth.passkeys() });
};

const performPasskeyActivation = async ({ activationToken, friendlyName }) => {
    const options = await beginPasskeyActivationRequest({
        activationToken,
        friendlyName
    });
    const credential = await registerPasskey(options?.optionsJson);

    return completePasskeyActivationRequest({
        activationToken,
        operationId: options?.operationId,
        credential
    });
};

const performPasskeyEnrollment = async ({ friendlyName }) => {
    const options = await beginPasskeyEnrollmentRequest({
        friendlyName
    });
    const credential = await registerPasskey(options?.optionsJson);

    return completePasskeyEnrollmentRequest({
        operationId: options?.operationId,
        credential
    });
};

const performPasskeyLogin = async ({ username }) => {
    const options = await beginPasskeyLoginRequest({
        username
    });
    const credential = await authenticateWithPasskey(options?.optionsJson);

    return completePasskeyLoginRequest({
        operationId: options?.operationId,
        credential
    });
};

export const useActivatePasskeyMutation = (successCallback = null, errorCallback = null) => {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: performPasskeyActivation,
        onSuccess: (data) => {
            setAuthenticatedSession(qc, data, successCallback);
            setAccountPasskeyConfigured(qc);
            refreshPasskeyAccountState(qc);
        },
        onError: (error) => {
            if (errorCallback) {
                errorCallback(error);
            }
        }
    });
};

export const useEnrollPasskeyMutation = (successCallback = null, errorCallback = null) => {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: performPasskeyEnrollment,
        onSuccess: (data) => {
            setAuthenticatedSession(qc, data, successCallback);
            setAccountPasskeyConfigured(qc);
            refreshPasskeyAccountState(qc);
        },
        onError: (error) => {
            if (errorCallback) {
                errorCallback(error);
            }
        }
    });
};

export const usePasskeyLoginMutation = (
    successCallback = null,
    errorCallback = null,
    options = {}
) => {
    const qc = useQueryClient();
    const { applySessionOnSuccess = true } = options;

    return useMutation({
        mutationFn: performPasskeyLogin,
        onSuccess: (data) => {
            const finalizeSession = () => {
                applyAuthSession(data);
                qc.setQueryData(keys.auth.session(), data);
            };

            if (!applySessionOnSuccess) {
                if (successCallback) {
                    successCallback(data, finalizeSession);
                }

                return;
            }

            finalizeSession();
            if (successCallback) {
                successCallback(data);
            }
        },
        onError: (error) => {
            if (errorCallback) {
                errorCallback(error);
            }
        }
    });
};

export const useVerifyPasswordLoginCodeMutation = (
    successCallback = null,
    errorCallback = null,
    options = {}
) => {
    const qc = useQueryClient();
    const { applySessionOnSuccess = true } = options;

    return useMutation({
        mutationFn: verifyPasswordLoginCodeRequest,
        onSuccess: (data) => {
            const finalizeSession = () => {
                applyAuthSession(data);
                qc.setQueryData(keys.auth.session(), data);
            };

            if (!applySessionOnSuccess) {
                if (successCallback) {
                    successCallback(data, finalizeSession);
                }

                return;
            }

            finalizeSession();
            if (successCallback) {
                successCallback(data);
            }
        },
        onError: (error) => {
            if (errorCallback) {
                errorCallback(error);
            }
        }
    });
};

export const useResendPasswordLoginCodeMutation = (successCallback = null, errorCallback = null) => {
    return useMutation({
        mutationFn: resendPasswordLoginCodeRequest,
        onSuccess: (data) => {
            if (successCallback) {
                successCallback(data);
            }
        },
        onError: (error) => {
            if (errorCallback) {
                errorCallback(error);
            }
        }
    });
};

export const useRecoveryCodeLoginMutation = (successCallback = null, errorCallback = null) => {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: recoveryCodeLoginRequest,
        onSuccess: (data) => {
            setAuthenticatedSession(qc, data, successCallback);
        },
        onError: (error) => {
            if (errorCallback) {
                errorCallback(error);
            }
        }
    });
};

export const useUpdateCurrentAccountMutation = (successCallback = null, errorCallback = null) => {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: updateCurrentAccountRequest,
        onSuccess: (data) => {
            qc.setQueryData(keys.auth.account(), data);
            useUserStore.getState().updateProfile(data);

            if (successCallback) {
                successCallback(data);
            }
        },
        onError: (error) => {
            if (errorCallback) {
                errorCallback(error);
            }
        }
    });
};

export const useChangeCurrentPasswordMutation = (successCallback = null, errorCallback = null) => {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: changeCurrentPasswordRequest,
        onSuccess: (data) => {
            applyAuthSession(data);
            qc.setQueryData(keys.auth.session(), data);
            qc.invalidateQueries({ queryKey: keys.auth.account() });
            qc.invalidateQueries({ queryKey: keys.auth.passkeys() });

            if (successCallback) {
                successCallback(data);
            }
        },
        onError: (error) => {
            if (errorCallback) {
                errorCallback(error);
            }
        }
    });
};

export const usePasskeySecuritySummaryQuery = (enabled = true) => {
    return useQuery({
        queryKey: keys.auth.passkeys(),
        queryFn: getPasskeySecuritySummaryRequest,
        enabled
    });
};

export const useDeletePasskeyMutation = (successCallback = null, errorCallback = null) => {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: deletePasskeyRequest,
        onSuccess: (data, credentialId) => {
            qc.invalidateQueries({ queryKey: keys.auth.passkeys() });

            if (successCallback) {
                successCallback(data, credentialId);
            }
        },
        onError: (error) => {
            if (errorCallback) {
                errorCallback(error);
            }
        }
    });
};

export const useRegenerateRecoveryCodesMutation = (successCallback = null, errorCallback = null) => {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: regenerateRecoveryCodesRequest,
        onSuccess: (data) => {
            qc.invalidateQueries({ queryKey: keys.auth.passkeys() });

            if (successCallback) {
                successCallback(data);
            }
        },
        onError: (error) => {
            if (errorCallback) {
                errorCallback(error);
            }
        }
    });
};

export const useLogoutMutation = (successCallback = null, errorCallback = null) => {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: logoutRequest,
        onSuccess: (data) => {
            if (successCallback) {
                successCallback(data);
            }
        },
        onError: (error) => {
            if (errorCallback) {
                errorCallback(error);
            }
        },
        onSettled: () => {
            useAuthSessionStore.getState().closeReauthModal();
            clearAuthSession();
            qc.clear();
        }
    });
};

export const useLogoutAllSessionsMutation = (successCallback = null, errorCallback = null) => {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: logoutAllSessionsRequest,
        onSuccess: (data) => {
            useAuthSessionStore.getState().closeReauthModal();
            clearAuthSession();
            qc.clear();

            if (successCallback) {
                successCallback(data);
            }
        },
        onError: (error) => {
            if (errorCallback) {
                errorCallback(error);
            }
        }
    });
};
